<template>
    <v-container fluid>
        <v-toolbar color="white" elevation="3" class="elegant-toolbar" height="72" density="comfortable">
            <v-toolbar-title class="toolbar-title">
                <span class="title-full">{{ $t('menu.commute.info') }}</span>
                <span class="title-short">Mobility</span>
            </v-toolbar-title>

            <v-select :label="$t('menu.commute.city')" :items="cities" v-model="selectedCity"
                class="city-select-enhanced" density="comfortable" hide-details variant="outlined">
                <template v-slot:prepend-inner>
                    <v-icon size="18" color="primary">mdi-map-marker-outline</v-icon>
                </template>
            </v-select>

            <template v-if="!isSmallScreen">
                <v-chip class="ma-2 weather-summary" color="blue lighten-5" label variant="outlined">
                    <v-icon start color="blue darken-2">mdi-weather-partly-cloudy</v-icon>
                    <span>{{ currentWeather.temp }}°C</span>
                </v-chip>
                <v-chip class="ma-2" color="primary" label>
                    <v-icon icon="mdi-clock-outline" start></v-icon>
                    <span class="font-weight-medium">{{ currentTime }}</span>
                </v-chip>
            </template>
            <v-card v-else class="combined-weather-time ma-2">
                <v-chip color="primary" label size="small" class="mb-1 time-chip">
                    <v-icon icon="mdi-clock-outline" start></v-icon>
                    <span class="font-weight-medium">{{ currentTime }}</span>
                </v-chip>
                <v-chip color="blue lighten-5" label size="small" class="weather-chip">
                    <v-icon start color="blue darken-2">mdi-weather-partly-cloudy</v-icon>
                    <span>{{ currentWeather.temp }}°C</span>
                </v-chip>
            </v-card>
        </v-toolbar>

        <v-container fluid>
            <v-row>
                <v-col cols="12" sm="6" lg="4" md="4">
                    <TransportModes :title="$t('menu.commute.s-bahn')" :icon="sbahn" :items="sTrains" mode="s-bahn"
                        :isLoading="isLoadingTransport" />
                    <TransportModes :title="$t('menu.commute.u-bahn')" :icon="ubahn" :items="uBahn" mode="u-bahn"
                        class="mt-1" :isLoading="isLoadingTransport" />
                    <TransportModes :title="$t('menu.commute.bus')" icon="mdi-bus" :items="buses" mode="bus"
                        class="mt-1" :isLoading="isLoadingTransport" />
                </v-col>
                <v-col cols="12" sm="6" lg="3" md="4">
                    <MobilityModeCard :title="$t('menu.commute.car-sharing')" mode="CAR SHARING" :items="carSharing"
                        :isLoading="isLoading" />
                    <MobilityModeCard :title="$t('menu.commute.taxi-services')" mode="TAXI SERVICE"
                        :items="taxiServices" :isLoading="isLoading" />
                    <MobilityModeCard :title="$t('menu.commute.scooter-sharing')" mode="SCOOTER SHARING"
                        :items="scooterSharing" :isLoading="isLoading" class="mt-1" />
                    <MobilityModeCard :title="$t('menu.commute.bike-sharing')" mode="BIKE SHARING" :items="bikeSharing"
                        :isLoading="isLoading" class="mt-1" />
                </v-col>
                <v-col cols="12" sm="12" lg="5" md="4" style="z-index: 1;">
                    <CommuteMap :center="center" :zoom="zoom" :isMapLoading="isMapLoading"
                        :serviceMarkers="serviceMarkers" :transitMarkers="transitMarkers" :centerMarker="centerMarker"
                        :isLoading="isLoading" :isLoadingTransport="isLoadingTransport" @map-ready="onMapReady"
                        ref="commuteMapRef" />
                </v-col>
            </v-row>
        </v-container>
    </v-container>
</template>

<script setup>
import { ref, onMounted, watch, nextTick, onBeforeUnmount, computed } from 'vue'
import axios from 'axios'
import { getMobilityServicesForCity } from '@/components/general/Commute/commuteServices/mobilityService'
import L from 'leaflet'
import sbahn from './commuteIcons/sbahn.svg'
import ubahn from './commuteIcons/ubahn.svg'
import deer from './commuteIcons/deer.svg'
import TransportModes from './TransportModes.vue'
import MobilityModeCard from './MobilityModeCard.vue'
import CommuteMap from './CommuteMap.vue'

const cities = ['Hamburg', 'Berlin', 'Frankfurt', 'Munich']
const CITY_COORDS = {
    Hamburg: { lat: 53.5529, lon: 10.005 },
    Berlin: { lat: 52.5251, lon: 13.3694 },
    Munich: { lat: 48.1402, lon: 11.5586 },
    Frankfurt: { lat: 50.1071, lon: 8.6638 },
}

// State
const selectedCity = ref('Hamburg')
const currentTime = ref('')
const commuteMapRef = ref(null)
const zoom = ref(13)
const center = ref([CITY_COORDS.Hamburg.lat, CITY_COORDS.Hamburg.lon])
const centerMarker = ref(null)
const serviceMarkers = ref([])
const transitMarkers = ref([])
const sTrains = ref([])
const uBahn = ref([])
const buses = ref([])
const carSharing = ref([])
const taxiServices = ref([])
const scooterSharing = ref([])
const bikeSharing = ref([])
const isLoading = ref(true)
const isLoadingTransport = ref(true)
const isMapLoading = ref(true)
const windowWidth = ref(window.innerWidth)
const isSmallScreen = computed(() => windowWidth.value < 650)

// Weather
const apiKey = '4ae793a3dd93a074ff16d7e97b06ec5b'
const currentWeather = ref({ temp: 0 })

// Debounce function
const debounce = (fn, ms) => {
    let timeoutId
    return (...args) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => fn(...args), ms)
    }
}

// Window resize handler
const handleResize = debounce(() => {
    windowWidth.value = window.innerWidth
    if (commuteMapRef.value?.map?.leafletObject) {
        commuteMapRef.value.map.leafletObject.invalidateSize()
    }
}, 100)

window.addEventListener('resize', handleResize)

// Update time
const updateTime = () => {
    const now = new Date()
    currentTime.value = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
}

const fetchWeather = async () => {
    try {
        const response = await fetch(
            `https://api.openweathermap.org/data/2.5/weather?q=${selectedCity.value}&appid=${apiKey}&units=metric`
        )
        const data = await response.json()
        currentWeather.value.temp = data.main.temp.toFixed(1)
    } catch (error) {
        console.error('Weather fetch failed:', error)
    }
}

const createHtmlIcon = (svgUrl, width = 30, height = 30, isCenter = false, name = '') => {
    let iconUrl = svgUrl

    return L.divIcon({
        html: `
      <div style="
        width: ${width}px; 
        height: ${height}px; 
        background-image: url('${iconUrl}');
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        border-radius: ${isCenter ? '50%' : '20%'};
        border: 2px solid ${isCenter ? '#ff6b6b' : '#4ecdc4'};
        background-color: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      " title="${name}"></div>
    `,
        className: isCenter ? 'center-marker-html' : 'service-marker-html',
        iconSize: [width, height],
        iconAnchor: [width / 2, height / 2],
        popupAnchor: [0, -height / 2],
    })
}

const createTransitIcon = (mode, line) => {
    let color = '#4ecdc4'
    let symbol = '🚊'

    if (line?.startsWith('S')) {
        color = '#009440'
        symbol = 'S'
    } else if (line?.startsWith('U')) {
        color = '#0066AD'
        symbol = 'U'
    } else if (mode === 'bus' || line?.toLowerCase().includes('bus')) {
        color = '#A3195B'
        symbol = '🚌'
    }

    return L.divIcon({
        html: `
      <div style="
        width: 32px; 
        height: 32px; 
        background-color: ${color};
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        font-weight: bold;
        font-size: 14px;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      ">${symbol}</div>
    `,
        className: 'transit-marker',
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -16],
    })
}

const fetchDepartures = async () => {
    try {
        isLoadingTransport.value = true
        const baseUrl = window.location.hostname === 'localhost'
            ? 'http://localhost:3001/departures'
            : 'https://pinestack-mobility.onrender.com/departures';
        const { data } = await axios.get(
            `${baseUrl}?city=${selectedCity.value.toLowerCase()}`
        )

        const S = [], U = [], B = [], transitStations = []
        for (const item of data.departures) {
            const line = item.line || ''
            if (line.startsWith('S')) S.push(item)
            else if (line.startsWith('U')) U.push(item)
            else if (line.toLowerCase().includes('bus') || line.match(/^Bus\s?/i)) B.push(item)

            if (item.latitude && item.longitude) {
                transitStations.push({
                    station: item.station,
                    line: item.line,
                    mode: item.mode,
                    destination: item.destination,
                    time: new Date(item.time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
                    coordinates: { lat: item.latitude, lon: item.longitude },
                })
            }
        }

        sTrains.value = S
        uBahn.value = U
        buses.value = B

        const sStations = transitStations.filter(s => s.line?.startsWith('S')).slice(0, 3)
        const uStations = transitStations.filter(s => s.line?.startsWith('U')).slice(0, 3)
        const busStations = transitStations.filter(s => s.mode === 'bus' || s.line?.toLowerCase().includes('bus')).slice(0, 3)

        transitMarkers.value = [...sStations, ...uStations, ...busStations].map((station, index) => ({
            position: [station.coordinates.lat, station.coordinates.lon],
            icon: createTransitIcon(station.mode, station.line),
            id: `transit-${index}`,
            station: station.station,
            line: station.line,
            mode: station.mode,
            time: station.time,
        }))
    } catch (error) {
        console.error('Failed to fetch departures:', error)
    } finally {
        isLoadingTransport.value = false
    }
}

const fetchMobility = async () => {
    try {
        isLoading.value = true
        const data = await getMobilityServicesForCity(selectedCity.value.toLowerCase())

        if (data) {
            carSharing.value = data.carSharing || []
            taxiServices.value = data.taxiServices || []
            scooterSharing.value = data.scooterSharing || []
            bikeSharing.value = data.bikeSharing || []

            if (data.centerCoords) {
                centerMarker.value = {
                    position: [data.centerCoords.lat, data.centerCoords.lon],
                    icon: createHtmlIcon(deer, 40, 40, true, 'Center'),
                }
            }

            const allServices = [
                ...(data.carSharing || []).map(item => ({ ...item, type: 'Car Sharing' })),
                ...(data.taxiServices || []).map(item => ({ ...item, type: 'Taxi Service' })),
                ...(data.scooterSharing || []).map(item => ({ ...item, type: 'Scooter Sharing' })),
                ...(data.bikeSharing || []).map(item => ({ ...item, type: 'Bike Sharing' })),
            ]

            serviceMarkers.value = allServices.map((item, index) => ({
                position: [item.coordinates.lat, item.coordinates.lon],
                icon: createHtmlIcon(item.logo, 28, 28, false, item.name),
                id: `service-${index}`,
                name: item.name,
                type: item.type,
            }))
        }
    } catch (error) {
        console.error('Failed to fetch mobility services:', error)
    } finally {
        isLoading.value = false
    }
}

const checkAllDataLoaded = async () => {
    await nextTick()
    if (!isLoadingTransport.value && !isLoading.value) {
        setTimeout(() => {
            isMapLoading.value = false
        }, 300)
    }
}

watch([isLoadingTransport, isLoading], checkAllDataLoaded)

const onMapReady = () => {
    // Map is ready, can perform additional actions if needed
    console.log('Map is ready!')
}

watch(selectedCity, async (newCity) => {
    isMapLoading.value = true
    centerMarker.value = null
    serviceMarkers.value = []
    transitMarkers.value = []
    sTrains.value = []
    uBahn.value = []
    buses.value = []
    carSharing.value = []
    taxiServices.value = []
    scooterSharing.value = []
    bikeSharing.value = []

    const coords = CITY_COORDS[newCity]
    center.value = [coords.lat, coords.lon]
    zoom.value = 13

    await Promise.all([fetchDepartures(), fetchMobility(), fetchWeather()])
})

onMounted(async () => {
    updateTime()
    await Promise.all([fetchDepartures(), fetchMobility(), fetchWeather()])

    const intervals = [
        setInterval(updateTime, 30000),
        setInterval(fetchDepartures, 60000),
        setInterval(fetchMobility, 300000),
    ]

    onBeforeUnmount(() => {
        intervals.forEach(clearInterval)
        window.removeEventListener('resize', handleResize)
    })
})
</script>

<style scoped>
.elegant-toolbar {
    backdrop-filter: blur(20px) saturate(180%);
    background: linear-gradient(135deg, rgba(248, 250, 252, 0.98), rgba(241, 245, 249, 0.95));
    border-bottom: 1px solid rgba(226, 232, 240, 0.6);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
    z-index: 10;
}

.city-select-enhanced {
    min-width: 160px;
    max-width: 200px;
}

.city-select-enhanced :deep(.v-field) {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.06), rgba(147, 197, 253, 0.04));
    border: 1.5px solid rgba(59, 130, 246, 0.15);
    border-radius: 14px;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
    margin-right: 10px;
}

.combined-weather-time {
    background: transparent;
    box-shadow: none;
    border: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-width: 70px;
    padding: 0;
}

.toolbar-title {
    font-size: 1.25rem;
    font-weight: 500;
    margin-right: 8px;
    white-space: nowrap;
}

.title-full {
    display: inline;
}

.title-short {
    display: none;
}

@media (max-width: 960px) {
    .city-select-enhanced {
        min-width: 120px;
    }
}

@media (max-width: 600px) {
    .title-full {
        display: none;
    }

    .title-short {
        display: inline;
    }

    .toolbar-title {
        font-size: 1.1rem;
    }

    .city-select-enhanced {
        min-width: 100px;
    }
}

@media (max-width: 480px) {
    .city-select-enhanced {
        min-width: 85px;
    }
}
</style>